/**
 * Launcher Controller - Shows/hides the Zendesk launcher and INDpopup based on scroll position and footer visibility
 * Mirrors the sticky-buy-button behavior for consistency across all pages
 */
class LauncherController {
  constructor() {
    // Skip on product pages - they use sticky-buy-button logic
    if (document.body.classList.contains('is--product')) return;

    this.scrollThreshold = 400;
    this.isVisible = false;
    this.footerVisible = false;
    this.footerObserver = null;

    this.init();
  }

  init() {
    this.setupScrollListener();
    this.setupFooterObserver();
    this.alignINDButton();
  }

  /**
   * Align the IND accessibility button with Zendesk chat (bottom: 20px)
   * Must use JS because the button is inside a shadow DOM
   */
  alignINDButton() {
    const applyStyles = () => {
      const host = document.querySelector('#INDShadowRootHost');
      const menuBtn = host?.shadowRoot?.querySelector('#INDmenu-btn');
      if (menuBtn) {
        menuBtn.style.setProperty('bottom', '20px', 'important');
        return true;
      }
      return false;
    };

    // Try immediately
    if (applyStyles()) return;

    // Poll for element (equalweb loads async)
    let attempts = 0;
    const interval = setInterval(() => {
      if (applyStyles() || ++attempts > 50) {
        clearInterval(interval);
      }
    }, 100);
  }

  setupScrollListener() {
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Check initial scroll position
    this.handleScroll();
  }

  setupFooterObserver() {
    const footer = document.querySelector('footer, .footer, [data-section-type="footer"]');
    if (!footer) return;

    this.footerObserver = new IntersectionObserver(
      (entries) => {
        this.footerVisible = entries[0].isIntersecting;
        if (this.footerVisible) {
          this.hide();
        } else if (window.scrollY > this.scrollThreshold) {
          this.show();
        }
      },
      { threshold: 0, rootMargin: '0px 0px 200px 0px' }
    );

    this.footerObserver.observe(footer);
  }

  handleScroll() {
    const scrollY = window.scrollY;

    if (scrollY > this.scrollThreshold && !this.footerVisible) {
      this.show();
    } else if (scrollY <= this.scrollThreshold) {
      this.hide();
    }
  }

  show() {
    if (!this.isVisible) {
      document.body.classList.add('launcher-visible');
      this.isVisible = true;
    }
  }

  hide() {
    if (this.isVisible) {
      document.body.classList.remove('launcher-visible');
      this.isVisible = false;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new LauncherController());
} else {
  new LauncherController();
}
